import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { initialSources } from "../data";
import type { OpsSnapshot, SourceStatus } from "../types";

const fallbackSnapshot = (): OpsSnapshot => ({
  state: "waiting",
  generatedAt: new Date().toISOString(),
  sources: initialSources,
  data: null,
});

const isSourceStatus = (value: unknown): value is SourceStatus => {
  if (!value || typeof value !== "object") return false;
  const source = value as Partial<SourceStatus>;
  return (
    typeof source.id === "string" &&
    typeof source.label === "string" &&
    ["connected", "delayed", "waiting", "error"].includes(source.state ?? "") &&
    typeof source.message === "string"
  );
};

const isSnapshot = (value: unknown): value is OpsSnapshot => {
  if (!value || typeof value !== "object") return false;
  const snapshot = value as Partial<OpsSnapshot>;
  return (
    ["connected", "delayed", "waiting", "error"].includes(snapshot.state ?? "") &&
    typeof snapshot.generatedAt === "string" &&
    Array.isArray(snapshot.sources) &&
    snapshot.sources.every(isSourceStatus) &&
    (snapshot.data === null || typeof snapshot.data === "object")
  );
};

export const OpsContext = createContext<OpsSnapshot>(fallbackSnapshot());

export const useOpsSnapshot = () => useContext(OpsContext);

export function useOps() {
  const [snapshot, setSnapshot] = useState<OpsSnapshot>(fallbackSnapshot);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const response = await fetch("/api/ops", {
        headers: { accept: "application/json" },
        cache: "no-store",
      });
      if (!response.ok) throw new Error(`ops api ${response.status}`);
      const payload: unknown = await response.json();
      setSnapshot(isSnapshot(payload) ? payload : fallbackSnapshot());
    } catch {
      setSnapshot(fallbackSnapshot());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), 30_000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  return { snapshot, loading, refresh };
}

export function useKstClock() {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  const dateParts = new Intl.DateTimeFormat("ko-KR", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
  }).formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    dateParts.find((item) => item.type === type)?.value ?? "";
  const date = `${part("year")}.${part("month")}.${part("day")} ${part("weekday")}`;

  const time = new Intl.DateTimeFormat("ko-KR", {
    timeZone: "Asia/Seoul",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(now);

  return { date, time };
}
